<template>
  <div>
    <div
      v-if="showChooseOptionMessage"
      class="text-md p-3 mt-2 bg-gray-10 text-center"
      v-html="bp('selectOption')"
    ></div>
    <div
      v-if="showEmptyStoreDataMessage"
      class="text-md p-3 mt-2 bg-gray-10 text-center"
      v-html="bp('notAvailableLocally')"
    ></div>
    <div
      id="lcly-button-0"
      data-switchlive="true"
      data-switchlive-impression="true"
      data-switchlive-impression-id-PL="1"
      :class="{hide: hideWidget}"
    >
      <a
        id="lcly-link-0"
        data-switchlive="true"
        data-switchlive-mode="auto"
        data-switchlive-id-PL="6"
        href="https://brands.locally.com"
        target="_blank"
      ></a>
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import headTag from '@/utils/head-tag';
import {mapState} from 'vuex';

export default {
  props: {
    options: {
      type: Object,
      default: () => null,
    },
    product: {
      type: Object,
      default: () => null,
    },
    variant: {
      type: Object,
      default: () => null,
      required: true,
    },
    showLocallyWidget: {
      type: Boolean,
      default: true,
    },
    radius: {
      type: Object,
      default: () => ({
        miles: '200',
        km: '200',
      }),
    },
  },
  data() {
    return {
      storeData: {},
      showLink: true,
      hideWidget: true,
      showChooseOptionMessage: false,
      isLoading: false,
      configurationQueue: [],
    };
  },
  computed: {
    locallyConfig() {
      const config = {
        company_name: this.options.companyName,
        button_text: ':heart:',
        button_id: 'HTML',
        company_id: this.options.companyId,
        css: '6',
        color_0: '#999999',
        upc: this.selectedVariantUPC,
        n_related_styles: '3',
        show_location_switcher: '1',
        show_location_prompt: '1',
        lang: this.$store.state.locale.toLowerCase().replace(/_/g, '-'),
        show_dealers: '1',
        n_dealers: '2',
        show_only_upc_stocking_dealers: '1',
        link_opens_modal: '1',
        show_address: '1',
        show_directions: '1',
        show_phone: '1',
        no_link: this.product.isProprietaryBrand ? '0' : '1',
        hide_all_when_no_stock: '0',
        ...(this.getCountryCode(this.customer) && {only_show_country: this.getCountryCode(this.customer)}),
      };

      return config;
    },
    isVariantSelected() {
      return !isEmpty(this.variant);
    },
    selectedVariantUPC() {
      let upc = '';

      if (this.isVariantSelected) {
        upc = this.variant?.upc ?? '';
      } else if (this.product?.variants?.length === 1) {
        upc = this.product.variants[0]?.upc ?? '';
      }

      return upc;
    },
    locallyEndpoint() {
      const obj = this.locallyConfig;
      const locallyQuery = Object.keys(obj)
        .reduce((a, k) => {
          a.push(`${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`);
          return a;
        }, [])
        .join('&');
      return `${this.options.baseUrl}/stores/map.js?${locallyQuery}`;
    },
    showEmptyStoreDataMessage() {
      return (
        this.isVariantSelected &&
        this.storeData?.n_items_stocking_product === 0 &&
        this.storeData?.product_id > 0 &&
        this.hideWidget
      );
    },
    productVariants() {
      return this.product.variants.map((variant) => variant.upc).toString();
    },
    ...mapState(['locale']),
    ...mapState('user', ['customer']),
  },
  watch: {
    selectedVariantUPC: function () {
      this.reloadLocally({
        hide_all_when_no_stock: this.locallyConfig.hide_all_when_no_stock,
        no_link: this.locallyConfig.no_link,
        upc: this.variant.upc ?? '',
      });
    },
    variant: function () {
      if (this.isVariantSelected) {
        this.locallyConfig.upc = this.variant.upc;

        this.reloadLocally({
          upc: this.variant.upc ?? '',
        });
      }
    },
    customer(customer) {
      const countryCode = this.getCountryCode(customer);
      if (!countryCode) return;
      this.locallyConfig.only_show_country = countryCode;
      this.reloadLocally({
        ...(countryCode && {only_show_country: countryCode}),
        upc: this.variant.upc ?? '',
      });
    },
    showLink: function () {
      this.reloadLocally({
        hide_all_when_no_stock: this.locallyConfig.hide_all_when_no_stock,
        no_link: this.locallyConfig.no_link,
        upc: this.variant.upc ?? '',
      });
    },
    showLocallyWidget: function () {
      this.reloadLocally({
        hide_all_when_no_stock: this.locallyConfig.hide_all_when_no_stock,
        no_link: this.locallyConfig.no_link,
        upc: this.variant.upc ?? '',
      });
    },
  },
  mounted() {
    this.setUpLocallyRadius();
    this.appendLocallyScript();
  },
  destroyed: function () {
    this.locallyData();
  },
  methods: {
    reloadLocally(props) {
      if (!isFunction(window.lcly_reload_0)) {
        //add this configuration to the queue of staged configuration changes
        this.configurationQueue = [...this.configurationQueue, props];
        return;
      }

      if (props?.upc && !this.isLoading) {
        this.isLoading = true;
        return window.lcly_reload_0(props);
      }
    },
    appendLocallyScript() {
      headTag.appendAsyncScript('lcly-script-0', this.locallyEndpoint);
      headTag.appendAsyncScript(
        'lcly-script-switchlive-0',
        `${this.options.baseUrl}/js/switchlive.js?company_id=${this.options.companyId}`
      );
      headTag.appendAsyncScript(
        'lcly-script-switchlive-events-0',
        `${this.options.baseUrl}/switchlive/switchlive-events.js?company_id=${this.options.companyId}`
      );
      this.locallyData();
    },
    locallyData() {
      window.addEventListener('LOCALLY_data_update', this.storeDataLoad, false);
    },
    storeDataLoad(event) {
      this.storeData = event?.detail ?? {};
      this.hideWidget = !this.isVariantSelected;
      this.showChooseOptionMessage = !this.isVariantSelected;

      this.locallyConfig.hide_all_when_no_stock = '0';

      let hasChanged = true;
      if (!this.isVariantSelected) {
        this.showLink = true;
      } else {
        if (isEmpty(this.storeData) || (this.storeData.n_items_stocking_product === 0 && this.storeData.product_id)) {
          this.hideWidget = false;
          this.showLink = false;
        } else if (!this.storeData.product_id) {
          this.locallyConfig.hide_all_when_no_stock = '1';
          hasChanged = false;
        } else {
          this.hideWidget = false;
          this.showLink = true;
        }
      }
      this.$emit('change-locally-widget', hasChanged);
      this.isLoading = false;

      //If there are items in the config queue, trigger a reload
      if (this.configurationQueue.length > 0) {
        this.reloadLocally(this.processQueue(this.configurationQueue));
        this.configurationQueue = [];
      }
    },
    setUpLocallyRadius() {
      const kmLocales = ['en_ca', 'fr_ca'];
      if (kmLocales.includes(this.$store.state.locale.toLowerCase())) {
        this.locallyConfig.radius_in_km = this.radius.km;
      } else {
        this.locallyConfig.radius_in_miles = this.radius.miles;
      }
    },
    getCountryCode(customer) {
      return this.locale.includes('IN_TL') ? customer?.geolocation?.countryCode : customer?.countryCode;
    },
    processQueue(queue) {
      const aggregatedConfig = queue.reduce((prevConfig, currentConfig) => {
        return {...prevConfig, ...currentConfig};
      }, {});
      return aggregatedConfig;
    },
  },
};
</script>
